// =============================================================================
// HOOK POUR PRÉPARER ET TÉLÉCHARGER LES DONNÉES DES PDFs
// Charge automatiquement toutes les données nécessaires depuis Supabase
// IMPORTANT: Tous les calculs de rentabilité sont effectués en HT
// =============================================================================

import { useState, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { RapportChantierPDF } from '@/components/pdf/RapportChantierPDF';
import { BilanCloturePDF } from '@/components/pdf/BilanCloturePDF';
import type { PDFData } from '@/components/pdf/pdfUtils';
import {
  getStatutRentabilite,
  BENCHMARKS_BTP,
} from '@/lib/calculs-rentabilite';
import { getDateFinEffective, AffectationRetard, SuspensionInfo } from '@/lib/dateUtils';

interface UsePDFDataReturn {
  isLoading: boolean;
  error: string | null;
  telechargerRapport: (entrepriseNom?: string) => Promise<void>;
  telechargerBilan: (entrepriseNom?: string, dateFinReelle?: string) => Promise<void>;
  chargerDonneesPDF: () => Promise<PDFData | null>;
}

export function usePDFData(chantierId: string): UsePDFDataReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Charger toutes les données nécessaires pour le PDF
  const chargerDonneesPDF = useCallback(async (): Promise<PDFData | null> => {
    if (!chantierId) return null;

    try {
      if (import.meta.env.DEV) console.log('[usePDFData] Chargement des données pour le chantier:', chantierId);

      // 1. CHANTIER + DEVIS
      const { data: chantier, error: errChantier } = await supabase
        .from('chantiers')
        .select(`
          *,
          devis:devis(id, montant_ht, montant_ttc, tva, version, statut, actif)
        `)
        .eq('id', chantierId)
        .single();

      if (errChantier || !chantier) {
        console.error('[usePDFData] Erreur chantier:', errChantier);
        throw new Error('Chantier non trouvé');
      }

      // Devis actif
      const devisActif = chantier.devis?.find((d: any) => d.actif) || chantier.devis?.[0];
      const montantDevisHT = devisActif?.montant_ht || chantier.budget_ht || 0;
      const montantDevisTTC = devisActif?.montant_ttc || montantDevisHT * 1.2;

      if (import.meta.env.DEV) {
        console.log('[usePDFData] Chantier:', chantier.nom_chantier || chantier.nom);
        console.log('[usePDFData] Devis HT:', montantDevisHT);
      }

      // 2. MÉTRIQUES TEMPS RÉEL (stockées dans colonne JSONB 'metrics')
      const { data: metricsRow } = await supabase
        .from('chantier_metrics_realtime')
        .select('metrics')
        .eq('chantier_id', chantierId)
        .maybeSingle();

      // Extraire les métriques du JSONB
      const metrics = metricsRow?.metrics as Record<string, any> | null;

      if (import.meta.env.DEV) console.log('[usePDFData] Métriques:', metrics);

      // 3. FACTURES FOURNISSEURS
      const { data: factures } = await supabase
        .from('factures_fournisseurs')
        .select('*')
        .eq('chantier_id', chantierId);

      const totalFactures = factures?.reduce((sum, f) => sum + (f.montant_ht || 0), 0) || 0;
      if (import.meta.env.DEV) console.log('[usePDFData] Factures:', factures?.length || 0, '| Total:', totalFactures);

      // 4. FRAIS CHANTIER (utiliser montant_ht si disponible, sinon montant_total comme HT)
      // IMPORTANT: Exclure les frais de type "Coût retard" car ils sont comptés séparément
      const { data: fraisBruts } = await supabase
        .from('frais_chantier')
        .select('*')
        .eq('chantier_id', chantierId);

      // Filtrer les frais de type "Coût retard" (pour éviter le double comptage)
      // Utiliser .includes() pour capturer tous les cas (ex: "Coût retard (main d'œuvre) ⏳")
      const frais = fraisBruts?.filter(f => {
        const typeLower = f.type_frais?.toLowerCase() || '';
        return !typeLower.includes('retard') && !typeLower.includes('pénalité');
      }) || [];

      // Priorité au montant_ht, sinon on considère montant_total comme étant HT
      const totalFrais = frais?.reduce((sum, f) => sum + (f.montant_ht ?? f.montant_total ?? 0), 0) || 0;
      if (import.meta.env.DEV) console.log('[usePDFData] Frais HT (hors retard):', frais?.length || 0, '| Total:', totalFrais);

      // 5. AFFECTATIONS ÉQUIPE (via la vue ou la table)
      const { data: affectations } = await supabase
        .from('affectations_chantiers')
        .select(`
          *,
          membre:membres_equipe(id, prenom, nom, poste, taux_horaire, coefficient_charges, heures_par_jour)
        `)
        .eq('chantier_id', chantierId);

      // Calculer les coûts MO en AGRÉGEANT PAR MEMBRE UNIQUE
      // Un même membre peut avoir plusieurs affectations (période initiale + retard)
      const membresMap = new Map<string, {
        nom: string;
        poste: string;
        jours: number;
        heures: number;
        cout: number;
        membreId: string;
      }>();

      affectations?.forEach(a => {
        const membreId = a.membre_equipe_id || a.id; // ID unique du membre
        const membreNom = a.membre ? `${a.membre.prenom || ''} ${a.membre.nom || ''}`.trim() : 'Membre';
        const tauxHoraire = a.taux_horaire_specifique || a.membre?.taux_horaire || 20;
        const coeffCharges = a.membre?.coefficient_charges || 1.85;
        const heuresParJour = a.heures_par_jour || a.membre?.heures_par_jour || 8;

        // Calculer les jours réellement travaillés depuis jours_details si disponible
        let joursAffectation = 0;
        if (a.jours_details && Array.isArray(a.jours_details) && a.jours_details.length > 0) {
          // Somme des valeurs (0.5 = demi-journée, 1 = journée complète)
          joursAffectation = a.jours_details.reduce((sum: number, jd: any) => sum + (jd.value || 1), 0);
        } else {
          joursAffectation = a.jours_travailles || 0;
        }

        // Calculer les heures en tenant compte des demi-journées
        let heuresAffectation = 0;
        if (a.jours_details && Array.isArray(a.jours_details) && a.jours_details.length > 0) {
          a.jours_details.forEach((jd: any) => {
            const jourValue = jd.value || 1;
            heuresAffectation += jourValue < 1 ? heuresParJour / 2 : heuresParJour;
          });
        } else {
          heuresAffectation = joursAffectation * heuresParJour;
        }

        const coutAffectation = Math.round(heuresAffectation * tauxHoraire * coeffCharges * 100) / 100;

        // Agréger par membre unique
        if (membresMap.has(membreId)) {
          const existing = membresMap.get(membreId)!;
          existing.jours += joursAffectation;
          existing.heures += heuresAffectation;
          existing.cout += coutAffectation;
        } else {
          membresMap.set(membreId, {
            nom: membreNom,
            poste: a.membre?.poste || 'Non défini',
            jours: joursAffectation,
            heures: heuresAffectation,
            cout: coutAffectation,
            membreId,
          });
        }
      });

      // Convertir la Map en array pour le PDF
      const equipeMembres = Array.from(membresMap.values()).map(m => ({
        nom: m.nom,
        poste: m.poste,
        jours: Math.round(m.jours * 10) / 10, // Arrondi à 1 décimale
        heures: Math.round(m.heures),
        cout: Math.round(m.cout * 100) / 100,
      }));

      const totalMO = equipeMembres.reduce((sum, m) => sum + m.cout, 0);
      const totalHeures = equipeMembres.reduce((sum, m) => sum + m.heures, 0);
      const totalJours = equipeMembres.reduce((sum, m) => sum + m.jours, 0);

      if (import.meta.env.DEV) console.log('[usePDFData] Équipe:', equipeMembres.length, '| Total MO:', totalMO);

      // 6. FACTURATION CLIENT — Source unique de vérité via RPC get_paiement_totals
      const { data: paiementTotalsData } = await supabase
        .rpc('get_paiement_totals', { p_chantier_id: chantierId });
      const pt = paiementTotalsData?.[0];

      const totalPaye = pt?.total_encaisse ?? 0;
      const totalFacture = pt?.total_a_encaisser ?? montantDevisHT;

      if (import.meta.env.DEV) console.log('[usePDFData] Facturation client (RPC): Encaissé:', totalPaye, '| À encaisser:', totalFacture, '| %:', pt?.pourcentage_encaisse);

      // 7. CALCULS FINAUX
      const totalDepenses = totalFactures + totalFrais + totalMO;
      const margeEuros = metrics?.marge_finale ?? (montantDevisHT - totalDepenses);
      // Utiliser marge_finale_pct de la DB si disponible, sinon recalculer
      const margePct = metrics?.marge_finale_pct ?? (montantDevisHT > 0 ? (margeEuros / montantDevisHT) * 100 : 0);
      const dureeEstimee = metrics?.duree_estimee_jours || chantier.duree_estimee_jours || chantier.duree_estimee || 1;
      // Calculer le coût journalier depuis les données réelles si pas dans metrics
      const coutJournalierCalcule = totalJours > 0 ? Math.round(totalDepenses / totalJours) : 0;

      // 8. CALCUL DES JOURS DE RETARD ET COÛT RETARD
      // Utiliser les affectations de type "Retard chantier" (source unique de vérité)
      const affectationsRetardLocales = affectations?.filter(
        a => a.notes && a.notes.startsWith('Retard chantier')
      ) || [];

      // Dédupliquer les jours de retard par date:period unique
      // (plusieurs membres peuvent travailler le même jour, ça compte pour 1 jour unique)
      const joursRetardUniquesSet = new Set<string>();
      affectationsRetardLocales.forEach(a => {
        if (a.jours_details && Array.isArray(a.jours_details)) {
          a.jours_details.forEach((jd: any) => {
            joursRetardUniquesSet.add(`${jd.date}:${jd.period || 'FULL'}`);
          });
        }
      });

      // Calculer les jours uniques (FULL=1, AM/PM=0.5)
      let joursRetardTravailles = 0;
      joursRetardUniquesSet.forEach(key => {
        const period = key.split(':')[1];
        joursRetardTravailles += (period === 'FULL') ? 1 : 0.5;
      });

      // Coût de retard = somme des coûts de chaque affectation retard
      let coutRetard = 0;
      affectationsRetardLocales.forEach(a => {
        const tauxHoraire = a.taux_horaire_specifique || a.membre?.taux_horaire || 20;
        const coeffCharges = a.membre?.coefficient_charges || 1.85;
        const heuresParJour = a.heures_par_jour || a.membre?.heures_par_jour || 8;
        const heures = (a.jours_travailles || 0) * heuresParJour;
        coutRetard += heures * tauxHoraire * coeffCharges;
      });

      if (import.meta.env.DEV) console.log('[usePDFData] Jours retard (affectations Retard chantier):', joursRetardTravailles, '| Coût retard:', coutRetard);

      // 8b. CALCUL DES JOURS DE REPRISE ET COÛT REPRISE
      // Utiliser les affectations de type "Reprise chantier" (source unique de vérité)
      const affectationsRepriseLocales = affectations?.filter(
        a => a.notes === 'Reprise chantier'
      ) || [];

      // Dédupliquer les jours de reprise par date:period unique
      const joursRepriseUniquesSet = new Set<string>();
      affectationsRepriseLocales.forEach(a => {
        if (a.jours_details && Array.isArray(a.jours_details)) {
          a.jours_details.forEach((jd: any) => {
            joursRepriseUniquesSet.add(`${jd.date}:${jd.period || 'FULL'}`);
          });
        }
      });

      // Calculer les jours uniques (FULL=1, AM/PM=0.5)
      let joursRepriseTravailles = 0;
      joursRepriseUniquesSet.forEach(key => {
        const period = key.split(':')[1];
        joursRepriseTravailles += (period === 'FULL') ? 1 : 0.5;
      });

      // Coût de reprise = somme des coûts de chaque affectation reprise
      let coutReprise = 0;
      affectationsRepriseLocales.forEach(a => {
        const tauxHoraire = a.taux_horaire_specifique || a.membre?.taux_horaire || 20;
        const coeffCharges = a.membre?.coefficient_charges || 1.85;
        const heuresParJour = a.heures_par_jour || a.membre?.heures_par_jour || 8;
        const heures = (a.jours_travailles || 0) * heuresParJour;
        coutReprise += heures * tauxHoraire * coeffCharges;
      });

      if (import.meta.env.DEV) console.log('[usePDFData] Jours reprise (affectations Reprise chantier):', joursRepriseTravailles, '| Coût reprise:', coutReprise);

      // 9. SUSPENSIONS
      const { data: suspensionsData } = await supabase
        .from('suspensions_chantiers')
        .select('*')
        .eq('chantier_id', chantierId)
        .order('date_suspension', { ascending: false });

      const suspensionsList = suspensionsData || [];
      const suspensionsTotalJours = suspensionsList
        .filter(s => s.date_reprise) // Seulement les suspensions terminées
        .reduce((sum, s) => sum + (s.duree_suspension_jours || 0), 0);
      const suspensionsTotalCout = suspensionsList
        .reduce((sum, s) => sum + (s.cout_mobilisation || 0), 0);

      if (import.meta.env.DEV) console.log('[usePDFData] Suspensions:', suspensionsList.length, '| Total jours:', suspensionsTotalJours, '| Coût:', suspensionsTotalCout);

      // Libellés des raisons
      const RAISONS_LABELS: Record<string, string> = {
        intemperies: 'Intempéries',
        attente_materiaux: 'Attente matériaux',
        probleme_client: 'Problème client',
        autre: 'Autre',
      };

      if (import.meta.env.DEV) {
        console.log('[usePDFData] ═══════════════════════════════════════');
        console.log('[usePDFData] Total dépenses HT:', totalDepenses);
        console.log('[usePDFData] Marge HT:', margeEuros, '€ (', margePct.toFixed(1), '%)');
        console.log('[usePDFData] ═══════════════════════════════════════');
      }

      // 10. CALCUL DATE FIN EFFECTIVE (avec retards et suspensions)
      const { data: affectationsRetardRaw } = await supabase
        .from('affectations_chantiers')
        .select('date_fin, jours_travailles, jours_details')
        .eq('chantier_id', chantierId)
        .like('notes', 'Retard chantier%');

      const affectationsRetard: AffectationRetard[] = (affectationsRetardRaw || []).map(a => ({
        date_fin: a.date_fin,
        jours_travailles: a.jours_travailles || 0,
        jours_details: a.jours_details as AffectationRetard['jours_details']
      }));

      // Utiliser les suspensions terminées déjà chargées
      const suspensionsTerminees: SuspensionInfo[] = suspensionsList
        .filter(s => s.date_reprise)
        .map(s => ({
          date_suspension: s.date_suspension,
          date_reprise: s.date_reprise,
          duree_suspension_jours: s.duree_suspension_jours
        }));

      const dateFinEffectiveCalc = getDateFinEffective({
        date_fin_estimee: chantier.date_fin_estimee,
        date_fin_initiale: chantier.date_fin_initiale,
        affectationsRetard,
        suspensionsTerminees
      });

      // Calculer la date depuis getDateFinEffective()
      let dateFinEffectiveStr = dateFinEffectiveCalc
        ? format(dateFinEffectiveCalc, 'yyyy-MM-dd')
        : null;

      // Prendre la MAX date_fin des affectations retard si elle est plus tardive
      // (car getDateFinEffective() peut ne pas utiliser directement date_fin de l'affectation)
      if (affectationsRetardRaw && affectationsRetardRaw.length > 0) {
        const maxDateFinRetard = affectationsRetardRaw.reduce(
          (max, a) => a.date_fin > max ? a.date_fin : max,
          affectationsRetardRaw[0].date_fin
        );
        if (!dateFinEffectiveStr || maxDateFinRetard > dateFinEffectiveStr) {
          dateFinEffectiveStr = maxDateFinRetard;
        }
      }

      // Prendre aussi la MAX date_fin des affectations reprise si elle est plus tardive
      if (affectationsRepriseLocales.length > 0) {
        const repriseAvecDateFin = affectationsRepriseLocales.filter(a => a.date_fin);
        if (repriseAvecDateFin.length > 0) {
          const maxDateFinReprise = repriseAvecDateFin.reduce(
            (max: string, a: any) => a.date_fin > max ? a.date_fin : max,
            repriseAvecDateFin[0].date_fin
          );
          if (!dateFinEffectiveStr || maxDateFinReprise > dateFinEffectiveStr) {
            dateFinEffectiveStr = maxDateFinReprise;
          }
        }
      }

      // Fallback si toujours pas de date
      if (!dateFinEffectiveStr) {
        dateFinEffectiveStr = chantier.date_fin_prevue || chantier.date_fin_estimee || new Date().toISOString();
      }

      if (import.meta.env.DEV) console.log('[usePDFData] Date fin effective (avec MAX retard):', dateFinEffectiveStr);

      // Construire l'objet PDFData
      const pdfData: PDFData = {
        chantier: {
          nom: chantier.nom_chantier || chantier.nom || 'Chantier sans nom',
          client: chantier.client || 'Non renseigné',
          adresse: chantier.adresse || chantier.adresse_chantier || 'Non renseignée',
          reference: chantier.reference_chantier || chantier.reference,
          date_debut: chantier.date_debut_reelle || chantier.date_debut_prevue || new Date().toISOString(),
          date_fin_prevue: dateFinEffectiveStr, // Utilise la date effective (avec retards/suspensions)
          date_fin_initiale: chantier.date_fin_initiale || chantier.date_fin_estimee, // Date avant retard
          date_fin_reelle: chantier.date_fin_reelle,
          duree_prevue: dureeEstimee,
          statut: chantier.etat_chantier || chantier.statut || 'en_cours',
        },
        devis: {
          montant_ht: montantDevisHT,
          montant_ttc: montantDevisTTC,
          tva: devisActif?.tva || 20,
          version: devisActif?.version || 'V1',
          statut: devisActif?.statut || 'brouillon',
        },
        depenses: {
          factures: factures?.map(f => ({
            fournisseur: f.fournisseur || 'Fournisseur',
            categorie: f.categorie || 'Autres',
            montant_ht: f.montant_ht || 0,
            date: f.date_facture || '',
          })) || [],
          frais: frais?.map(f => ({
            type: f.type_frais || 'Autres',
            description: f.description || '',
            // Priorité au montant_ht, sinon on considère montant_total comme HT
            montant: f.montant_ht ?? f.montant_total ?? 0,
            date: f.date_frais || '',
          })) || [],
          total_factures: totalFactures,
          total_frais: totalFrais,
          total_mo: totalMO,
          total: totalDepenses,
        },
        equipe: {
          membres: equipeMembres,
          total_jours: totalJours,
          total_heures: totalHeures,
          total_cout: totalMO,
          cout_horaire_moyen: totalHeures > 0 ? Math.round(totalMO / totalHeures) : 0,
        },
        facturation: {
          total_facture: totalFacture,
          total_paye: totalPaye,
          reste_a_facturer: totalFacture - totalPaye,
        },
        metriques: {
          marge_euros: margeEuros,
          marge_pct: margePct,
          roi: totalDepenses > 0 ? Math.round((margeEuros / totalDepenses) * 100) : 0,
          ca_jour: dureeEstimee > 0 ? Math.round(montantDevisHT / dureeEstimee) : 0,
          rentabilite_horaire: totalHeures > 0 ? Math.round(margeEuros / totalHeures) : 0,
          // Utiliser les seuils harmonisés de calculs-rentabilite.ts
          statut_rentabilite: metrics?.statut_rentabilite || getStatutRentabilite(margePct),
          budget_disponible: metrics?.budget_disponible ?? (montantDevisHT - totalDepenses),
          cout_journalier: metrics?.cout_journalier_equipe || coutJournalierCalcule,
          // Données de retard
          jours_retard_travailles: joursRetardTravailles,
          cout_retard: Math.round(coutRetard * 100) / 100,
          // Données de reprise
          jours_reprise_travailles: joursRepriseTravailles,
          cout_reprise: Math.round(coutReprise * 100) / 100,
        },
        // Utiliser les benchmarks BTP harmonisés
        benchmarks: BENCHMARKS_BTP,
        // Données de suspension
        suspensions: suspensionsList.length > 0 ? {
          suspensions: suspensionsList.map(s => ({
            date_suspension: s.date_suspension,
            date_reprise: s.date_reprise,
            duree_jours: s.duree_suspension_jours || 0,
            raison: RAISONS_LABELS[s.raison] || s.raison,
            type_suspension: s.type_suspension,
            cout_mobilisation: s.cout_mobilisation || 0,
          })),
          total_jours: suspensionsTotalJours,
          total_cout: suspensionsTotalCout,
          nb_suspensions: suspensionsList.length,
        } : undefined,
      };

      return pdfData;

    } catch (err) {
      console.error('[usePDFData] Erreur:', err);
      throw err;
    }
  }, [chantierId]);

  // Télécharger le Rapport de suivi (chantier en cours)
  const telechargerRapport = useCallback(async (entrepriseNom?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      toast({
        title: '⏳ Chargement...',
        description: 'Préparation des données du rapport...',
      });

      const data = await chargerDonneesPDF();
      if (!data) throw new Error('Impossible de charger les données');

      const blob = await pdf(
        <RapportChantierPDF data={data} entrepriseNom={entrepriseNom} />
      ).toBlob();

      // Téléchargement natif
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rapport_${data.chantier.nom.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: '📄 Rapport généré',
        description: 'Le rapport de suivi a été téléchargé avec succès.',
      });

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(message);
      toast({
        title: 'Erreur',
        description: `Impossible de générer le rapport : ${message}`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [chargerDonneesPDF, toast]);

  // Télécharger le Bilan de clôture (chantier terminé)
  const telechargerBilan = useCallback(async (entrepriseNom?: string, dateFinReelle?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      toast({
        title: '⏳ Chargement...',
        description: 'Préparation des données du bilan...',
      });

      const data = await chargerDonneesPDF();
      if (!data) throw new Error('Impossible de charger les données');

      const blob = await pdf(
        <BilanCloturePDF data={data} entrepriseNom={entrepriseNom} dateFinReelle={dateFinReelle} />
      ).toBlob();

      // Téléchargement natif
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Bilan_Cloture_${data.chantier.nom.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: '📄 Bilan généré',
        description: 'Le bilan de clôture a été téléchargé avec succès.',
      });

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(message);
      toast({
        title: 'Erreur',
        description: `Impossible de générer le bilan : ${message}`,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [chargerDonneesPDF, toast]);

  return {
    isLoading,
    error,
    telechargerRapport,
    telechargerBilan,
    chargerDonneesPDF,
  };
}
